import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Trash2,
  RotateCw,
  Search,
  Check,
  X,
  Info,
  Plus,
  Sparkles,
  AlertTriangle,
  RefreshCw,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { socialAPI, getErrorMessage } from '../../services/api';
import AccountTypeBadge, {
  CONNECTABLE_VARIANTS,
} from './AccountTypeBadge';
import TypePickerModal, { TYPE_PICKER_OPTIONS } from './TypePickerModal';

interface AccountMetadata {
  page_name?: string | null;
  page_id?: string | null;
  connection_type?: string | null;
  followers_count?: number | null;
  instagram_business_account_id?: string | null;
  has_page_token?: boolean;
}

type TokenStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'token_missing'
  | 'disconnected';

interface ConnectedAccount {
  id: string;
  platform: string;
  /** Normalized account type from the backend — Phase A of the
   * social-account-types work. See
   * ``docs/social-account-types-tracking.md``. ``null`` means the row
   * predates the column and hasn't been backfilled yet; the UI renders
   * it as "Unknown type" rather than guessing. */
  account_type?: string | null;
  username: string;
  display_name: string;
  avatar_url: string;
  profile_url: string;
  is_active: boolean;
  last_synced: string | null;
  token_status?: TokenStatus;
  can_publish?: boolean;
  needs_reauth?: boolean;
  token_expires_at?: string | null;
  token_status_reason?: string | null;
  metadata?: AccountMetadata;
}

interface InstagramSelectionOption {
  id: string;
  username?: string;
  display_name?: string;
  avatar_url?: string;
  account_type?: string;
  page_id?: string;
  page_name?: string;
  followers_count?: number | null;
}

type FilterType = 'all' | 'social' | 'messengers' | 'blogs';

interface PlatformDef {
  id: string;
  name: string;
  icon: string;
  /** Background CSS for the brand square (gradients allowed) */
  brandClass: string;
  category: FilterType;
}

const PLATFORMS: PlatformDef[] = [
  { id: 'facebook', name: 'Facebook', icon: 'f', brandClass: 'bg-[#1877f2]', category: 'social' },
  { id: 'instagram', name: 'Instagram', icon: 'Ig', brandClass: 'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af]', category: 'social' },
  { id: 'twitter', name: 'X (Twitter)', icon: '𝕏', brandClass: 'bg-[#0f1419]', category: 'social' },
  { id: 'tiktok', name: 'TikTok', icon: '♪', brandClass: 'bg-[#010101]', category: 'social' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'in', brandClass: 'bg-[#0a66c2]', category: 'social' },
  { id: 'youtube', name: 'YouTube', icon: '▶', brandClass: 'bg-[#ff0000]', category: 'social' },
  { id: 'pinterest', name: 'Pinterest', icon: 'P', brandClass: 'bg-[#bd081c]', category: 'social' },
  { id: 'threads', name: 'Threads', icon: '@', brandClass: 'bg-[#101010]', category: 'social' },
  { id: 'bluesky', name: 'Bluesky', icon: '🦋', brandClass: 'bg-[#0a7aff]', category: 'social' },
  { id: 'telegram', name: 'Telegram', icon: '✈', brandClass: 'bg-[#229ed9]', category: 'messengers' },
  { id: 'tumblr', name: 'Tumblr', icon: 'T', brandClass: 'bg-[#36465d]', category: 'blogs' },
  { id: 'wordpress', name: 'WordPress', icon: 'W', brandClass: 'bg-[#21759b]', category: 'blogs' },
  { id: 'gmb', name: 'Google Business', icon: 'G', brandClass: 'bg-[#ea4335]', category: 'social' },
];

const FILTER_TAB_IDS: FilterType[] = ['all', 'social', 'messengers', 'blogs'];

const findPlatform = (id: string): PlatformDef | undefined =>
  PLATFORMS.find((p) => p.id === id);

const getPlatformHint = (id: string): string => {
  if (id === 'bluesky') {
    return 'App password';
  }
  if (id === 'telegram') {
    return 'Bot token';
  }
  return 'Auto posting · OAuth';
};

// ---------------------------------------------------------------------------
// Avatar — platform icon or user image with green "live" dot
// ---------------------------------------------------------------------------

interface AvatarProps {
  account: ConnectedAccount;
  platform: PlatformDef | undefined;
  failed: boolean;
  onError: () => void;
  size?: number;
}

const AccountAvatar: React.FC<AvatarProps> = ({
  account,
  platform,
  failed,
  onError,
  size = 32,
}) => {
  const dim = `${size}px`;
  const showImage = account.avatar_url && !failed;
  return (
    <div className="relative flex-shrink-0">
      {showImage ? (
        <img
          src={account.avatar_url}
          alt=""
          style={{ width: dim, height: dim }}
          className="rounded-n-sm object-cover"
          onError={onError}
        />
      ) : (
        <div
          style={{ width: dim, height: dim }}
          className={`${platform?.brandClass || 'bg-n-text-md'} rounded-n-sm flex items-center justify-center text-white text-[13px] font-bold`}
        >
          {platform?.icon}
        </div>
      )}
      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-n-green rounded-full border-2 border-n-card" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const SocialAccountsPage: React.FC = () => {
  const { t } = useTranslation();
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);

  // Filter tab labels
  const filterTabLabels: Record<FilterType, string> = {
    all: t('accounts_filter_all', 'All'),
    social: t('accounts_filter_social', 'Social networks'),
    messengers: t('accounts_filter_messengers', 'Messengers'),
    blogs: t('accounts_filter_blogs', 'Blogs'),
  };
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [credentialModal, setCredentialModal] = useState<{
    platform: string;
    fields: { key: string; label: string; placeholder: string; type?: string }[];
  } | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [instagramModal, setInstagramModal] = useState(false);
  // Phase C generic type picker — opens for TikTok / LinkedIn / YouTube
  // / Pinterest before their OAuth redirect. Instagram keeps its
  // dedicated modal because its variants drive different OAuth configs.
  const [typePickerPlatform, setTypePickerPlatform] = useState<string | null>(
    null,
  );
  const [typePickerSelection, setTypePickerSelection] = useState<string>('');
  const [instagramSelection, setInstagramSelection] = useState<{
    token: string;
    options: InstagramSelectionOption[];
    selectedId: string;
    loading: boolean;
  } | null>(null);
  const [justConnectedPlatform, setJustConnectedPlatform] = useState<string | null>(null);
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle OAuth callback redirect
  useEffect(() => {
    const connected = searchParams.get('connected');
    const success = searchParams.get('success');
    const selectionToken = searchParams.get('selection_token');
    if (connected) {
      if (connected === 'instagram' && success === 'select' && selectionToken) {
        setInstagramSelection({
          token: selectionToken,
          options: [],
          selectedId: '',
          loading: true,
        });
        socialAPI
          .getInstagramSelection(selectionToken)
          .then((response) => {
            const options = response.data.options || [];
            setInstagramSelection({
              token: selectionToken,
              options,
              selectedId: options[0]?.id || '',
              loading: false,
            });
          })
          .catch((err: unknown) => {
            toast.error(
              getErrorMessage(
                err,
                t(
                  'accounts_instagram_selection_load_failed',
                  'Instagram selection expired. Please reconnect Instagram.',
                ),
              ),
            );
            setInstagramSelection(null);
          });
      } else if (success === 'true') {
        const platformName = connected.charAt(0).toUpperCase() + connected.slice(1);
        toast.success(
          t('accounts_connected_success', {
            platform: platformName,
            defaultValue: `${platformName} connected successfully!`,
          }),
        );
        queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });
        setJustConnectedPlatform(connected);
      } else {
        const platformName = connected.charAt(0).toUpperCase() + connected.slice(1);
        const errorMsg = searchParams.get('error') || t('accounts_unknown_error', 'Unknown error');
        toast.error(
          t('accounts_connect_failed', {
            platform: platformName,
            error: errorMsg,
            defaultValue: `Failed to connect ${platformName}: ${errorMsg}`,
          }),
        );
      }
      // Clear the URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient, t]);

  const { data: accountsData, isLoading } = useQuery({
    queryKey: ['socialAccounts'],
    queryFn: () => socialAPI.list().then((r) => r.data),
    staleTime: 30_000,
  });

  const disconnectMutation = useMutation({
    mutationFn: (accountId: string) => socialAPI.disconnect(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });
      toast.success(t('accounts_disconnected', 'Account disconnected'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('accounts_disconnect_failed', 'Failed to disconnect account')));
    },
  });

  const syncMutation = useMutation({
    mutationFn: (accountId: string) => socialAPI.sync(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });
      toast.success(t('accounts_synced', 'Account synced'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('accounts_sync_failed', 'Failed to sync account')));
    },
  });

  const completeInstagramSelectionMutation = useMutation({
    mutationFn: ({
      token,
      selectedId,
    }: {
      token: string;
      selectedId: string;
    }) => socialAPI.completeInstagramSelection(token, selectedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });
      toast.success(t('accounts_instagram_connected', 'Instagram connected successfully!'));
      setJustConnectedPlatform('instagram');
      setInstagramSelection(null);
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(
          error,
          t('accounts_instagram_selection_failed', 'Failed to connect Instagram account'),
        ),
      );
    },
  });

  const handleConnect = async (platformId: string) => {
    // Instagram shows account type selector modal
    if (platformId === 'instagram') {
      setInstagramModal(true);
      return;
    }

    // Phase C: platforms with a generic type picker (TikTok /
    // LinkedIn / YouTube / Pinterest) show the picker modal before
    // OAuth. The chosen variant flows through ``socialAPI.connect``
    // as ``?type={variant}`` — the backend signs it into the OAuth
    // state and honors it on callback (with server-side verification
    // for LinkedIn Company).
    if (TYPE_PICKER_OPTIONS[platformId]) {
      const defaultChoice =
        TYPE_PICKER_OPTIONS[platformId]!.find((o) => o.recommended)?.key ||
        TYPE_PICKER_OPTIONS[platformId]![0]!.key;
      setTypePickerPlatform(platformId);
      setTypePickerSelection(defaultChoice);
      return;
    }

    // Bluesky uses app password, not OAuth
    if (platformId === 'bluesky') {
      setCredentialModal({
        platform: 'bluesky',
        fields: [
          { key: 'handle', label: t('accounts_bluesky_handle_label', 'Handle'), placeholder: 'yourname.bsky.social' },
          { key: 'app_password', label: t('accounts_bluesky_password_label', 'App Password'), placeholder: 'xxxx-xxxx-xxxx-xxxx', type: 'password' },
        ],
      });
      setCredentialValues({});
      return;
    }

    // Telegram uses bot token
    if (platformId === 'telegram') {
      setCredentialModal({
        platform: 'telegram',
        fields: [
          { key: 'bot_token', label: t('accounts_telegram_token_label', 'Bot Token'), placeholder: '123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11' },
        ],
      });
      setCredentialValues({});
      return;
    }

    // Standard OAuth flow
    setConnectingPlatform(platformId);
    try {
      const response = await socialAPI.connect(platformId);
      const { oauth_url } = response.data;
      if (oauth_url) {
        window.location.href = oauth_url;
      } else {
        toast.error(t('accounts_oauth_url_failed', 'Failed to get OAuth URL'));
        setConnectingPlatform(null);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('accounts_connection_initiate_failed', 'Failed to initiate connection')));
      setConnectingPlatform(null);
    }
  };

  const handleCredentialSubmit = async () => {
    if (!credentialModal) {
      return;
    }
    setConnectingPlatform(credentialModal.platform);
    try {
      if (credentialModal.platform === 'bluesky') {
        await socialAPI.connectBluesky(credentialValues.handle || '', credentialValues.app_password || '');
      } else if (credentialModal.platform === 'telegram') {
        await socialAPI.connectTelegram(credentialValues.bot_token || '');
      }
      toast.success(
        t('accounts_credential_connected', {
          platform: credentialModal.platform.charAt(0).toUpperCase() + credentialModal.platform.slice(1),
          defaultValue: `${credentialModal.platform.charAt(0).toUpperCase() + credentialModal.platform.slice(1)} connected!`,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });
      setCredentialModal(null);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('accounts_connection_failed', 'Connection failed')));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleTypePickerConfirm = async () => {
    if (!typePickerPlatform || !typePickerSelection) {
      return;
    }
    setConnectingPlatform(typePickerPlatform);
    try {
      const response = await socialAPI.connect(
        `${typePickerPlatform}?type=${typePickerSelection}`,
      );
      const { oauth_url } = response.data;
      if (oauth_url) {
        window.location.href = oauth_url;
      } else {
        toast.error(
          t('accounts_oauth_url_failed', 'Failed to get OAuth URL'),
        );
        setConnectingPlatform(null);
      }
      setTypePickerPlatform(null);
    } catch (err: unknown) {
      toast.error(
        getErrorMessage(
          err,
          t(
            'accounts_connection_initiate_failed',
            'Failed to initiate connection',
          ),
        ),
      );
      setConnectingPlatform(null);
    }
  };

  const handleInstagramLink = async () => {
    setConnectingPlatform('instagram');
    try {
      const response = await socialAPI.connect('instagram?type=business_fb');
      const { oauth_url } = response.data;
      if (oauth_url) {
        window.location.href = oauth_url;
      } else {
        toast.error(t('accounts_oauth_url_failed', 'Failed to get OAuth URL'));
        setConnectingPlatform(null);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('accounts_connection_initiate_failed', 'Failed to initiate connection')));
      setConnectingPlatform(null);
    } finally {
      setInstagramModal(false);
    }
  };

  const accounts: ConnectedAccount[] = useMemo(
    () => accountsData?.accounts || [],
    [accountsData?.accounts],
  );

  // Dedup by compound key so the same platform can hold multiple
  // accounts of different types (e.g. TikTok Business alongside TikTok
  // Personal). Format: ``"{platform}:{account_type || '*'}"``.
  //
  // Why this matters: the old code hid the "Connect" entry the moment
  // any one account for a platform existed, so users couldn't add a
  // Business alongside a Personal even on platforms that support it.
  const connectedPlatformTypeKeys = useMemo(
    () =>
      new Set(
        accounts.map((a) => `${a.platform}:${a.account_type || '*'}`),
      ),
    [accounts],
  );

  /** Return true if at least one of the platform's connectable
   * variants hasn't been connected yet. Platforms not in
   * ``CONNECTABLE_VARIANTS`` fall back to the original single-
   * connection behavior (one account per platform). */
  const hasUnconnectedVariant = React.useCallback(
    (platformId: string): boolean => {
      const variants = CONNECTABLE_VARIANTS[platformId];
      if (!variants || variants.length === 0) {
        // Single-variant platform (Bluesky, Telegram, Threads, …):
        // "connected" iff any row exists, regardless of account_type.
        const anyConnected = accounts.some((a) => a.platform === platformId);
        return !anyConnected;
      }
      // Multi-variant platform: show the Connect entry as long as at
      // least one variant is still available.
      return variants.some(
        (v) => !connectedPlatformTypeKeys.has(`${platformId}:${v}`),
      );
    },
    [accounts, connectedPlatformTypeKeys],
  );

  const filteredPlatforms = useMemo(() => {
    return PLATFORMS.filter((p) => {
      const notConnected = hasUnconnectedVariant(p.id);
      const matchesCategory = activeFilter === 'all' || p.category === activeFilter;
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      return notConnected && matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchQuery, hasUnconnectedVariant]);

  const connectedAccounts = accounts.filter((a) => a.is_active);

  const reauthAccounts = useMemo(
    () => connectedAccounts.filter((a) => a.needs_reauth),
    [connectedAccounts],
  );
  const expiringSoonAccounts = useMemo(
    () => connectedAccounts.filter((a) => a.token_status === 'expiring_soon'),
    [connectedAccounts],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-n-page">
        <div className="text-[13px] text-n-text-lt">
          {t('accounts_loading', 'Loading accounts...')}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-n-page min-h-full">
      <div className="max-w-5xl mx-auto px-6 py-6">

        {/* ── Page header row ──────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <h1 className="text-[18px] font-semibold text-n-text tracking-tight">
              {t('accounts_page_title', 'Social Accounts')}
            </h1>
            <span className="text-[11px] font-medium text-n-text-md bg-n-hover px-2 py-0.5 rounded-n-xs">
              {t('accounts_count_summary', {
                connected: connectedAccounts.length,
                available: filteredPlatforms.length,
                defaultValue: `${connectedAccounts.length} connected · ${filteredPlatforms.length} available`,
              })}
            </span>
          </div>
        </div>

        {/* ── Re-auth required banner ──────────────────────── */}
        {reauthAccounts.length > 0 && (
          <div
            className="flex items-start gap-3 rounded-n bg-n-red-bg border border-n-red/30 px-3 py-2.5 mb-3"
            role="alert"
          >
            <div className="w-6 h-6 rounded-n-xs bg-n-red text-white flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-n-text">
                {t('accounts_reauth_required_title', {
                  count: reauthAccounts.length,
                  defaultValue:
                    reauthAccounts.length === 1
                      ? '1 account needs to be reconnected'
                      : `${reauthAccounts.length} accounts need to be reconnected`,
                })}
              </p>
              <p className="text-[12px] text-n-text-md mt-0.5">
                {t(
                  'accounts_reauth_required_body',
                  "Their access tokens have expired. Scheduled posts for these accounts won't publish until you reconnect.",
                )}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {reauthAccounts.map((a) => {
                  const platform = findPlatform(a.platform);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => handleConnect(a.platform)}
                      className="n-btn n-btn-secondary gap-1.5 px-2.5 py-1 text-[12px]"
                    >
                      <RefreshCw className="w-3 h-3" />
                      {t('accounts_reconnect_platform', {
                        platform: platform?.name || a.platform,
                        defaultValue: `Reconnect ${platform?.name || a.platform}`,
                      })}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Expiring-soon warning (non-blocking) ─────────── */}
        {reauthAccounts.length === 0 && expiringSoonAccounts.length > 0 && (
          <div
            className="flex items-start gap-3 rounded-n bg-n-amber-bg border border-n-amber/30 px-3 py-2.5 mb-3"
            role="status"
          >
            <div className="w-6 h-6 rounded-n-xs bg-n-amber text-white flex items-center justify-center flex-shrink-0 mt-0.5">
              <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-n-text">
                {t('accounts_expiring_soon_title', {
                  count: expiringSoonAccounts.length,
                  defaultValue:
                    expiringSoonAccounts.length === 1
                      ? '1 account is expiring soon'
                      : `${expiringSoonAccounts.length} accounts are expiring soon`,
                })}
              </p>
              <p className="text-[12px] text-n-text-md mt-0.5">
                {t(
                  'accounts_expiring_soon_body',
                  'Reconnect now to avoid a break in publishing when the tokens expire.',
                )}
              </p>
            </div>
          </div>
        )}

        {/* ── Just-connected callout ───────────────────────── */}
        {justConnectedPlatform && (
          <div
            className="flex items-center gap-3 rounded-n bg-n-green-bg border border-n-green/30 px-3 py-2.5 mb-5"
            role="status"
          >
            <div className="w-6 h-6 rounded-n-xs bg-n-green text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-n-text">
                {justConnectedPlatform.charAt(0).toUpperCase() + justConnectedPlatform.slice(1)}{' '}
                {t('accounts_connected_msg', 'connected successfully!')}
              </p>
              <p className="text-[12px] text-n-text-md">
                {t('accounts_can_now_publish', 'You can now publish content to this platform.')}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <Link
                to="/compose"
                className="n-btn n-btn-primary px-3 py-1.5 text-[12px]"
              >
                {t('accounts_create_post_btn', 'Create Post')}
              </Link>
              <Link
                to="/calendar"
                className="n-btn n-btn-secondary px-3 py-1.5 text-[12px]"
              >
                {t('accounts_calendar_btn', 'Calendar')}
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setJustConnectedPlatform(null)}
              className="n-btn-icon flex-shrink-0"
              aria-label={t('accounts_dismiss_banner', 'Dismiss notification')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ── Connected accounts section ───────────────────── */}
        {connectedAccounts.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-n-text-md whitespace-nowrap">
                {t('accounts_connected_accounts_title', 'Connected')}
              </h2>
              <div className="flex-1 h-px bg-n-border" />
              <span className="text-[11px] text-n-text-lt">
                {connectedAccounts.length} {t('accounts_active_label', 'active')}
              </span>
            </div>

            <div className="bg-n-card border border-n-border rounded-n overflow-hidden">
              {connectedAccounts.map((account, idx) => {
                const platform = findPlatform(account.platform);
                const isLast = idx === connectedAccounts.length - 1;
                return (
                  <div
                    key={account.id}
                    className={`group flex items-center gap-3 px-3.5 py-2.5 hover:bg-n-hover transition-colors ${
                      isLast ? '' : 'border-b border-n-border'
                    }`}
                  >
                    <AccountAvatar
                      account={account}
                      platform={platform}
                      failed={failedAvatars.has(account.id)}
                      onError={() =>
                        setFailedAvatars((prev) => {
                          const next = new Set(prev);
                          next.add(account.id);
                          return next;
                        })
                      }
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[13px] font-semibold text-n-text truncate">
                          {platform?.name || account.platform}
                        </p>
                        {/* Type badge rendered next to the platform
                            name so the user can tell Business apart
                            from Personal without clicking in. */}
                        <AccountTypeBadge accountType={account.account_type} />
                        <span className="text-n-text-lt text-[11px]">·</span>
                        <p className="text-[12px] text-n-text-lt truncate">
                          {account.display_name || account.username}
                        </p>
                      </div>
                      {account.metadata && (
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {account.needs_reauth && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-n-red bg-n-red-bg px-1.5 py-0.5 rounded-n-xs"
                              title={account.token_status_reason || undefined}
                            >
                              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} />
                              {t('accounts_needs_reauth_badge', 'Reconnect required')}
                            </span>
                          )}
                          {account.token_status === 'expiring_soon' && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-n-amber bg-n-amber-bg px-1.5 py-0.5 rounded-n-xs"
                              title={account.token_status_reason || undefined}
                            >
                              <Clock className="w-2.5 h-2.5" strokeWidth={2.5} />
                              {t('accounts_expiring_soon_badge', 'Expiring soon')}
                            </span>
                          )}
                          {account.metadata.page_name && (
                            <span className="inline-flex items-center text-[10px] font-medium text-n-blue bg-n-blue-bg px-1.5 py-0.5 rounded-n-xs">
                              {account.metadata.page_name}
                            </span>
                          )}
                          {account.metadata.connection_type && (
                            <span className="inline-flex items-center text-[10px] font-medium text-n-amber bg-n-amber-bg px-1.5 py-0.5 rounded-n-xs">
                              {account.metadata.connection_type}
                            </span>
                          )}
                          {account.metadata.followers_count !== null && account.metadata.followers_count !== undefined && account.metadata.followers_count > 0 && (
                            <span className="inline-flex items-center text-[10px] font-medium text-n-text-md bg-n-hover px-1.5 py-0.5 rounded-n-xs">
                              {account.metadata.followers_count.toLocaleString()}{' '}
                              {t('accounts_followers_label', 'followers')}
                            </span>
                          )}
                          {account.metadata.has_page_token && (
                            <span className="inline-flex items-center text-[10px] font-medium text-n-green bg-n-green-bg px-1.5 py-0.5 rounded-n-xs">
                              {t('accounts_publish_ready', 'Publish ready')}
                            </span>
                          )}
                          {account.metadata.instagram_business_account_id && (
                            <span className="inline-flex items-center text-[10px] font-medium text-n-blue bg-n-blue-bg px-1.5 py-0.5 rounded-n-xs">
                              IG {t('accounts_linked_label', 'linked')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions — reconnect is always visible when needed; others on hover */}
                    {account.needs_reauth && (
                      <button
                        type="button"
                        onClick={() => handleConnect(account.platform)}
                        disabled={connectingPlatform === account.platform}
                        className="n-btn n-btn-secondary gap-1 px-2 py-1 text-[11px] flex-shrink-0"
                        title={account.token_status_reason || t('accounts_reconnect_title', 'Reconnect account')}
                      >
                        <RefreshCw
                          className={`w-3 h-3 ${
                            connectingPlatform === account.platform ? 'animate-spin' : ''
                          }`}
                        />
                        {t('accounts_reconnect_btn', 'Reconnect')}
                      </button>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => syncMutation.mutate(account.id)}
                        disabled={syncMutation.isPending}
                        className="n-btn-icon disabled:opacity-40 disabled:cursor-not-allowed"
                        title={t('accounts_sync_title', 'Sync')}
                        aria-label={t('accounts_sync_title', 'Sync')}
                      >
                        <RotateCw
                          className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            confirm(
                              t('accounts_confirm_disconnect', {
                                platform: platform?.name || account.platform,
                                defaultValue: `Disconnect ${platform?.name || account.platform}?`,
                              }),
                            )
                          ) {
                            disconnectMutation.mutate(account.id);
                          }
                        }}
                        className="n-btn-icon hover:text-n-red"
                        title={t('accounts_disconnect_title', 'Disconnect')}
                        aria-label={t('accounts_disconnect_title', 'Disconnect')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Add new account section ──────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-n-text-md whitespace-nowrap">
              {t('accounts_add_new_account_title', 'Add new')}
            </h2>
            <div className="flex-1 h-px bg-n-border" />
          </div>

          {/* Filter tabs + search */}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="n-tab-bar">
              {FILTER_TAB_IDS.map((tabId) => {
                const isActive = activeFilter === tabId;
                return (
                  <button
                    key={tabId}
                    type="button"
                    onClick={() => setActiveFilter(tabId)}
                    className={`n-tab ${isActive ? 'n-tab-active' : ''}`}
                  >
                    {filterTabLabels[tabId]}
                  </button>
                );
              })}
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-text-lt pointer-events-none" />
              <input
                type="text"
                placeholder={t('accounts_search_platforms_placeholder', 'Search platforms...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="n-input pl-8 w-56"
                aria-label={t('accounts_search_platforms_placeholder', 'Search platforms...')}
              />
            </div>
          </div>

          {/* Platform grid */}
          {filteredPlatforms.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredPlatforms.map((platform) => {
                const isConnecting = connectingPlatform === platform.id;
                return (
                  <button
                    key={platform.id}
                    type="button"
                    onClick={() => {
                      if (!isConnecting) {
                        handleConnect(platform.id);
                      }
                    }}
                    disabled={isConnecting}
                    aria-busy={isConnecting}
                    className="group flex items-center gap-3 text-left px-3 py-2.5 rounded-n-sm bg-n-card border border-n-border hover:bg-n-hover hover:border-n-blue/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div
                      className={`${platform.brandClass} w-8 h-8 rounded-n-sm flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0`}
                    >
                      {platform.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-n-text truncate">
                        {platform.name}
                      </p>
                      <p className="text-[11px] text-n-text-lt truncate">
                        {isConnecting
                          ? t('accounts_connecting', 'Connecting...')
                          : getPlatformHint(platform.id)}
                      </p>
                    </div>
                    {isConnecting ? (
                      <RotateCw className="w-4 h-4 text-n-text-lt animate-spin flex-shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 text-n-text-lt group-hover:text-n-blue transition-colors flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="n-empty">
              <div className="n-empty-icon">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="n-empty-title">
                {t('accounts_no_platforms_found', 'No platforms found')}
              </div>
              <div className="n-empty-desc">
                {t('accounts_adjust_search_filter', 'Try adjusting your search or filter')}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Generic type picker (TikTok / LinkedIn / YouTube / Pinterest) ── */}
      {typePickerPlatform && (
        <TypePickerModal
          platformLabel={
            findPlatform(typePickerPlatform)?.name ?? typePickerPlatform
          }
          options={TYPE_PICKER_OPTIONS[typePickerPlatform] ?? []}
          selectedKey={typePickerSelection}
          onSelect={setTypePickerSelection}
          onConfirm={handleTypePickerConfirm}
          onClose={() => setTypePickerPlatform(null)}
          isSubmitting={connectingPlatform === typePickerPlatform}
        />
      )}

      {/* ── Instagram Page/account chooser after Facebook Login ── */}
      {instagramSelection && (
        <div
          className="n-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="instagram-selection-title"
        >
          <div className="n-modal max-w-2xl">
            <div className="n-modal-header">
              <div>
                <h3
                  id="instagram-selection-title"
                  className="text-[13px] font-semibold text-n-text"
                >
                  {t(
                    'accounts_instagram_selection_title',
                    'Choose the Instagram account to connect',
                  )}
                </h3>
                <p className="mt-0.5 text-[11px] text-n-text-md">
                  {t(
                    'accounts_instagram_selection_subtitle',
                    'Your Facebook login can manage more than one Page-linked professional Instagram account.',
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInstagramSelection(null)}
                className="n-btn-icon"
                aria-label={t('accounts_close_modal', 'Close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="n-modal-body">
              {instagramSelection.loading ? (
                <div className="flex items-center gap-2 rounded-n border border-n-border bg-n-page px-3 py-3 text-[12px] text-n-text-md">
                  <RotateCw className="w-4 h-4 animate-spin" />
                  {t(
                    'accounts_instagram_selection_loading',
                    'Loading Instagram accounts...',
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {instagramSelection.options.map((option) => {
                    const isSelected = instagramSelection.selectedId === option.id;
                    const accountTypeLabel =
                      option.account_type === 'instagram_creator'
                        ? t('accounts_creator_label', 'Creator')
                        : t('accounts_business_label', 'Business');
                    const title =
                      option.display_name ||
                      option.username ||
                      t('accounts_instagram_account_fallback', 'Instagram account');
                    const initial = (option.username || title).charAt(0).toUpperCase();

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() =>
                          setInstagramSelection((prev) =>
                            prev ? { ...prev, selectedId: option.id } : prev,
                          )
                        }
                        className={`w-full rounded-n border px-3 py-3 text-left transition-colors ${
                          isSelected
                            ? 'border-n-blue bg-n-blue-bg'
                            : 'border-n-border bg-n-card hover:bg-n-hover'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {option.avatar_url ? (
                            <img
                              src={option.avatar_url}
                              alt=""
                              className="w-10 h-10 rounded-n-sm object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-n-sm bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af] flex items-center justify-center text-white text-[15px] font-bold">
                              {initial}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-[13px] font-semibold text-n-text">
                                {title}
                              </p>
                              <span className="rounded-n-xs bg-n-green-bg px-1.5 py-0.5 text-[10px] font-semibold text-n-green">
                                {accountTypeLabel}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-[11px] text-n-text-md">
                              {option.username ? `@${option.username}` : option.id}
                              {option.page_name ? ` · ${option.page_name}` : ''}
                            </p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                              isSelected
                                ? 'border-n-blue bg-n-blue text-white'
                                : 'border-n-border bg-n-card text-transparent'
                            }`}
                          >
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </div>
                        </div>
                        {option.followers_count !== null &&
                          option.followers_count !== undefined && (
                            <p className="mt-2 pl-[52px] text-[11px] text-n-text-lt">
                              {t('accounts_followers_count_label', {
                                count: option.followers_count.toLocaleString(),
                                defaultValue: `${option.followers_count.toLocaleString()} followers`,
                              })}
                            </p>
                          )}
                      </button>
                    );
                  })}

                  {instagramSelection.options.length === 0 && (
                    <div className="rounded-n border border-n-border bg-n-page px-3 py-3 text-[12px] text-n-text-md">
                      {t(
                        'accounts_instagram_selection_empty',
                        'No selectable Instagram accounts were returned. Please reconnect Instagram.',
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="n-modal-footer">
              <button
                type="button"
                onClick={() => setInstagramSelection(null)}
                className="n-btn n-btn-secondary"
              >
                {t('accounts_cancel_btn', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!instagramSelection.selectedId) {
                    return;
                  }
                  completeInstagramSelectionMutation.mutate({
                    token: instagramSelection.token,
                    selectedId: instagramSelection.selectedId,
                  });
                }}
                disabled={
                  instagramSelection.loading ||
                  !instagramSelection.selectedId ||
                  completeInstagramSelectionMutation.isPending
                }
                aria-busy={completeInstagramSelectionMutation.isPending}
                className="n-btn n-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {completeInstagramSelectionMutation.isPending ? (
                  <span className="inline-flex items-center gap-1.5">
                    <RotateCw className="w-3.5 h-3.5 animate-spin" />
                    {t('accounts_connecting_label', 'Connecting...')}
                  </span>
                ) : (
                  t('accounts_instagram_selection_confirm', 'Connect selected account')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Instagram account type modal ─────────────────── */}
      {instagramModal && (
        <div
          className="n-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="instagram-modal-title"
        >
          <div className="n-modal max-w-xl">
            <div className="n-modal-header">
              <h3 id="instagram-modal-title" className="text-[13px] font-semibold text-n-text">
                {t('accounts_instagram_modal_title', 'Connect your Instagram account')}
              </h3>
              <button
                type="button"
                onClick={() => setInstagramModal(false)}
                className="n-btn-icon"
                aria-label={t('accounts_close_modal', 'Close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="n-modal-body">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="relative p-3 rounded-n border border-n-blue bg-n-blue-bg text-left">
                  <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-n-blue text-white flex items-center justify-center">
                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                  </div>
                  <span className="inline-block mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-n-green bg-n-green-bg px-1.5 py-0.5 rounded-n-xs">
                    {t('accounts_supported_badge', 'Supported')}
                  </span>
                  <h4 className="text-[12px] font-semibold text-n-text">
                    {t('accounts_ig_business_creator_label', 'Business / Creator')}
                  </h4>
                  <p className="text-[11px] text-n-text-md mt-0.5">
                    {t('accounts_ig_via_facebook', '(via Facebook)')}
                  </p>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-n-text-lt mt-2">
                    {t('accounts_auto_posting', 'Auto posting')}
                  </p>
                </div>

                <div className="p-3 rounded-n border border-n-border bg-n-hover/70 text-left opacity-75">
                  <div className="flex items-center gap-1">
                    <h4 className="text-[12px] font-semibold text-n-text">
                      {t('accounts_personal_label', 'Personal')}
                    </h4>
                    <Info className="w-3 h-3 text-n-text-lt" />
                  </div>
                  <p className="text-[11px] text-n-text-md mt-0.5">
                    {t(
                      'accounts_ig_personal_not_supported',
                      'Personal Instagram login cannot publish through the API.',
                    )}
                  </p>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-n-text-lt mt-2">
                    {t('accounts_not_available_label', 'Not available')}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-n-border">
                <button
                  type="button"
                  className="text-[12px] font-medium text-n-blue hover:underline"
                >
                  {t('accounts_learn_more_btn', 'Learn more')} →
                </button>
              </div>
            </div>

            <div className="n-modal-footer">
              <button
                type="button"
                onClick={() => setInstagramModal(false)}
                className="n-btn n-btn-secondary"
              >
                {t('accounts_cancel_btn', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleInstagramLink}
                disabled={connectingPlatform === 'instagram'}
                aria-busy={connectingPlatform === 'instagram'}
                className="n-btn n-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {connectingPlatform === 'instagram' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <RotateCw className="w-3.5 h-3.5 animate-spin" />
                    {t('accounts_linking', 'Linking...')}
                  </span>
                ) : (
                  t('accounts_link_btn', 'Link account')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Credential modal (Bluesky / Telegram) ────────── */}
      {credentialModal && (
        <div
          className="n-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="credential-modal-title"
        >
          <div className="n-modal max-w-md">
            <div className="n-modal-header">
              {(() => {
                const plat = findPlatform(credentialModal.platform);
                return (
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`${plat?.brandClass || 'bg-n-text-md'} w-8 h-8 rounded-n-sm flex items-center justify-center text-white text-[13px] font-bold`}
                    >
                      {plat?.icon}
                    </div>
                    <div>
                      <h3
                        id="credential-modal-title"
                        className="text-[13px] font-semibold text-n-text"
                      >
                        {t('accounts_connect_label', {
                          platform: plat?.name || credentialModal.platform,
                          defaultValue: `Connect ${plat?.name || credentialModal.platform}`,
                        })}
                      </h3>
                      <p className="text-[11px] text-n-text-md">
                        {credentialModal.platform === 'bluesky'
                          ? t('accounts_bluesky_instruction', 'Enter your Bluesky handle and an app password')
                          : t('accounts_telegram_instruction', 'Enter your Telegram bot token from @BotFather')}
                      </p>
                    </div>
                  </div>
                );
              })()}
              <button
                type="button"
                onClick={() => {
                  setCredentialModal(null);
                  setCredentialValues({});
                }}
                className="n-btn-icon"
                aria-label={t('accounts_close_modal', 'Close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="n-modal-body space-y-3">
              {credentialModal.fields.map((field) => (
                <div key={field.key}>
                  <label
                    htmlFor={`credential-${field.key}`}
                    className="n-label"
                  >
                    {field.label}
                  </label>
                  <input
                    id={`credential-${field.key}`}
                    type={field.type || 'text'}
                    placeholder={field.placeholder}
                    value={credentialValues[field.key] || ''}
                    onChange={(e) =>
                      setCredentialValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    className="n-input"
                  />
                </div>
              ))}

              {credentialModal.platform === 'bluesky' && (
                <p className="text-[11px] text-n-text-md bg-n-page border border-n-border rounded-n-sm px-2.5 py-2">
                  {t('accounts_bluesky_generate_password', 'Generate an app password at')}{' '}
                  <a
                    href="https://bsky.app/settings/app-passwords"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-n-blue hover:underline"
                  >
                    bsky.app/settings/app-passwords
                  </a>
                </p>
              )}

              {credentialModal.platform === 'telegram' && (
                <p className="text-[11px] text-n-text-md bg-n-page border border-n-border rounded-n-sm px-2.5 py-2">
                  {t('accounts_telegram_get_token', 'Create a bot and get your token from')}{' '}
                  <a
                    href="https://t.me/BotFather"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-n-blue hover:underline"
                  >
                    @BotFather
                  </a>{' '}
                  {t('accounts_telegram_on_telegram', 'on Telegram.')}
                </p>
              )}
            </div>

            <div className="n-modal-footer">
              <button
                type="button"
                onClick={() => {
                  setCredentialModal(null);
                  setCredentialValues({});
                }}
                className="n-btn n-btn-secondary"
              >
                {t('accounts_cancel_btn', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleCredentialSubmit}
                disabled={
                  connectingPlatform === credentialModal.platform ||
                  credentialModal.fields.some((f) => !credentialValues[f.key]?.trim())
                }
                aria-busy={connectingPlatform === credentialModal.platform}
                className="n-btn n-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {connectingPlatform === credentialModal.platform ? (
                  <span className="inline-flex items-center gap-1.5">
                    <RotateCw className="w-3.5 h-3.5 animate-spin" />
                    {t('accounts_connecting_label', 'Connecting...')}
                  </span>
                ) : (
                  t('accounts_connect_btn', 'Connect')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialAccountsPage;
